import React from 'react';
import { Link } from 'react-router';
import type {
  LscAutomationAccessLane,
  LscBillingStatusResponse,
  LscLeagueAutomationAccess,
} from '../types';
import { lscPath } from '../routing';

export interface AutomationAccessSummaryProps {
  leagueId: string;
  automationAccess: LscLeagueAutomationAccess | null;
  billingStatus?: LscBillingStatusResponse | null;
  loading?: boolean;
  customerFacing?: boolean;
}

const LANE_LABELS: Record<string, string> = {
  live_detection_eventsub: 'Twitch live alerts',
  live_truth_auto_enqueue: 'Live game verification',
  live_home_display: 'Live home display',
  live_promotion_fast_cadence: 'Fast live promotion',
  vod_handoff: 'Archive handoff',
  archive_finalization: 'Archive automation',
  archive_autopublish: 'Archive auto-publish',
  needs_attention_cleanup: 'Needs Attention cleanup',
  schedule_source_sync: 'Schedule source sync',
  vod_sync_backfill: 'Historical replay import',
  billing_entitlement: 'Billing entitlement',
};

export function automationEnrollmentStatusLabel(
  enrollment: NonNullable<LscLeagueAutomationAccess['enrollment']>
): string {
  switch (enrollment.status) {
    case 'enabled':
      return 'Automation enabled for this league';
    case 'pilot':
      return 'Automation access enabled';
    case 'preview':
      return 'Automation preview';
    case 'disabled':
      return 'Requires admin approval';
    default:
      return 'Enrollment status';
  }
}

export function automationEnrollmentSourceLabel(
  enrollment: NonNullable<LscLeagueAutomationAccess['enrollment']>
): string {
  switch (enrollment.source) {
    case 'legacy_pilot_fallback':
      return 'Enabled by league automation rules';
    case 'firestore':
      return 'Enrollment on file';
    case 'default_absent':
      return 'Preview only';
    default:
      return 'Enrollment is read-only';
  }
}

function enrollmentMismatchCount(
  enrollment: NonNullable<LscLeagueAutomationAccess['enrollment']>
): number {
  return Object.values(enrollment.lanes).filter((lane) => !lane.effective_matches_intent).length;
}

export function automationAccessLaneDisplayLabel(
  laneKey: string,
  lane: LscAutomationAccessLane,
  billingStatus?: LscBillingStatusResponse | null
): string {
  if (laneKey === 'billing_entitlement') {
    const entitlement = billingStatus?.entitlement;
    if (
      entitlement?.is_comped ||
      entitlement?.billing_cadence === 'comped' ||
      entitlement?.subscription_status === 'comped'
    ) {
      return 'Complimentary access';
    }
    if (lane.mode === 'enabled') {
      return lane.label;
    }
    if (lane.mode === 'preview' && billingStatus?.billing_model_enabled) {
      return 'Not subscription-gated';
    }
  }
  if (lane.requires_admin_enablement) {
    return 'Requires admin approval';
  }
  if (lane.requires_commissioner_action && lane.mode === 'blocked') {
    return 'Requires setup';
  }
  if (lane.mode === 'blocked' && laneKey === 'billing_entitlement') {
    return 'Requires subscription';
  }
  return lane.label;
}

function laneStatusClass(mode: LscAutomationAccessLane['mode']): string {
  switch (mode) {
    case 'enabled':
      return 'text-emerald-200';
    case 'preview':
    case 'dry_run':
      return 'text-amber-200';
    case 'blocked':
      return 'text-orange-200';
    default:
      return 'text-secondary-400';
  }
}

const AutomationAccessSummary: React.FC<AutomationAccessSummaryProps> = ({
  leagueId,
  automationAccess,
  billingStatus = null,
  loading = false,
  customerFacing = false,
}) => {
  if (loading || !automationAccess) {
    return null;
  }

  const enrollment = automationAccess.enrollment;
  const mismatchCount = enrollment ? enrollmentMismatchCount(enrollment) : 0;
  const showEnrollmentContext =
    Boolean(enrollment) &&
    (enrollment?.source !== 'default_absent' || mismatchCount > 0 || enrollment?.status !== 'disabled');

  const lanes = Object.entries(automationAccess.lanes).map(([key, lane]) => ({
    key,
    ...lane,
  }));
  const attentionLanes = lanes.filter(
    (lane) =>
      lane.mode !== 'enabled' ||
      lane.requires_admin_enablement ||
      lane.requires_commissioner_action
  );
  const showCompactWarning =
    automationAccess.readiness.is_complete &&
    !automationAccess.readiness.should_show_checklist &&
    attentionLanes.length > 0;

  const hideLaneGrid =
    !showCompactWarning &&
    automationAccess.overall_status === 'enabled' &&
    !showEnrollmentContext;

  if (hideLaneGrid) {
    return null;
  }

  const previewLanes = lanes.filter((lane) => lane.mode === 'preview' || lane.mode === 'dry_run');
  const adminLanes = lanes.filter((lane) => lane.requires_admin_enablement);

  return (
    <div
      className="rounded-xl border border-secondary-700 bg-secondary-900/40 px-4 py-3 space-y-3"
      data-testid="automation-access-summary"
    >
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
        <div>
          <p className="text-sm font-medium text-secondary-100">
            {customerFacing ? 'Automation lane details' : 'Automation access'}
          </p>
          <p className="text-xs text-secondary-400 mt-0.5">
            {customerFacing
              ? 'How each automation lane is running for your league.'
              : 'Read-only lane status — enrollment is read-only; automation is still controlled by existing safety gates.'}
          </p>
        </div>
        <Link
          to={lscPath(`/leagues/${leagueId}/settings`)}
          className="text-sm font-medium text-primary-300 hover:text-primary-200 shrink-0"
        >
          Automation settings
        </Link>
      </div>

      {showEnrollmentContext && enrollment ? (
        <div
          className="rounded-lg border border-secondary-800 bg-secondary-950/40 px-3 py-2 space-y-1"
          data-testid="automation-access-enrollment"
        >
          <p className="text-sm text-secondary-200">{automationEnrollmentStatusLabel(enrollment)}</p>
          <p className="text-xs text-secondary-500">{automationEnrollmentSourceLabel(enrollment)}</p>
          {mismatchCount > 0 ? (
            <p className="text-xs text-amber-200" data-testid="automation-access-enrollment-mismatch">
              Enrollment intent differs from active access on {mismatchCount} lane
              {mismatchCount === 1 ? '' : 's'}.
            </p>
          ) : null}
        </div>
      ) : null}

      {showCompactWarning ? (
        <p className="text-sm text-amber-100" data-testid="automation-access-compact-warning">
          Setup checklist is complete, but some automation lanes still need admin approval or
          commissioner policy changes.
        </p>
      ) : null}

      {!hideLaneGrid || showCompactWarning || attentionLanes.length > 0 ? (
      <ul className="grid gap-2 sm:grid-cols-2">
        {lanes.map((lane) => (
          <li
            key={lane.key}
            className="flex items-start justify-between gap-3 text-sm"
            data-testid={`automation-access-lane-${lane.key}`}
          >
            <span className="text-secondary-300">{LANE_LABELS[lane.key] ?? lane.key}</span>
            <span className={`shrink-0 ${laneStatusClass(lane.mode)}`}>
              {automationAccessLaneDisplayLabel(lane.key, lane, billingStatus)}
            </span>
          </li>
        ))}
      </ul>
      ) : null}

      {previewLanes.length > 0 ? (
        <p className="text-xs text-secondary-500" data-testid="automation-access-preview-note">
          Preview or dry-run lanes evaluate automation without applying production writes.
        </p>
      ) : null}

      {adminLanes.length > 0 ? (
        <p className="text-xs text-secondary-500" data-testid="automation-access-admin-note">
          {customerFacing
            ? 'Some lanes still require Scoutlytics enablement before they can run in production.'
            : 'Admin approval lanes require Scoutlytics operator enablement before apply paths unlock.'}
        </p>
      ) : null}
    </div>
  );
};

export default AutomationAccessSummary;
